import React from "react";
import styled, { css, keyframes } from "styled-components";
import { colors } from "src/theme/colors";
import { radii, text } from "src/theme/ui";

/**
 * Badge — the football web app's one chip, ported to this stack.
 *
 * That app has exactly one status chip: 10.5px at 600, 0.05em tracking,
 * uppercase, 3px/7px padding, a 5px corner, and a fill/text/edge triple from
 * one semantic family. It is what a status, a state or a label should be.
 *
 * The only other chip it draws is a **count** — a number beside a heading or
 * inside a control. A step shorter, not uppercase, and set in tabular figures
 * so a column of them lines up.
 *
 * There is deliberately no third size: several sizes is what produced chips a
 * few pixels apart on adjacent cards. If something needs to be bigger than a
 * chip, it is not a chip.
 *
 * `caps={false}` is the one escape, for a label that is a proper noun rather
 * than a state and would otherwise be shouting.
 */

export type BadgeTone =
  | "gray"
  | "brand"
  | "success"
  | "warning"
  | "error"
  | "info";

/* One entry per meaning. `gray` is the tone for finished, neutral, nothing to
   act on. */
const tones: Record<BadgeTone, ReturnType<typeof css>> = {
  gray: css`
    background: ${colors.gray[100]};
    color: ${colors.gray[600]};
    box-shadow: inset 0 0 0 1px ${colors.gray[200]};
  `,
  brand: css`
    background: ${colors.primary[10]};
    color: ${colors.primary[700]};
    box-shadow: inset 0 0 0 1px ${colors.primary[100]};
  `,
  success: css`
    background: ${colors.success[50]};
    color: ${colors.success[700]};
    box-shadow: inset 0 0 0 1px ${colors.success[100]};
  `,
  warning: css`
    background: ${colors.warning[50]};
    color: ${colors.warning[700]};
    box-shadow: inset 0 0 0 1px ${colors.warning[100]};
  `,
  error: css`
    background: ${colors.error[50]};
    color: ${colors.error[700]};
    box-shadow: inset 0 0 0 1px ${colors.error[100]};
  `,
  /* A state, so it is paint of its own rather than the brand's — otherwise a
     blue-accented tenant would show "pending" in the same hue as "brand". */
  info: css`
    background: #eff6ff;
    color: #1d4ed8;
    box-shadow: inset 0 0 0 1px #dbeafe;
  `,
};

const Root = styled.span<{
  $tone: BadgeTone;
  $caps: boolean;
  $count: boolean;
}>`
  display: inline-flex;
  width: fit-content;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  gap: 4px;
  overflow: hidden;
  padding: 3px 7px;
  border-radius: ${radii.sm}px;
  white-space: nowrap;
  font-weight: 600;

  ${({ $count, $caps }) =>
    $count
      ? css`
          padding: 2px 6px;
          font-size: 10px;
          ${text.numeric}
        `
      : css`
          font-size: 10.5px;
          letter-spacing: 0.05em;
          text-transform: ${$caps ? "uppercase" : "none"};
        `}

  ${({ $tone }) => tones[$tone]}

  svg {
    width: 12px;
    height: 12px;
  }
`;

/* The pulse marks something genuinely in flight, so it is opt-in: on every
   chip it would just be movement. */
const pulse = keyframes`
  0% { transform: scale(0.7); }
  70% { transform: scale(1.3); }
  100% { transform: scale(0.7); }
`;

const Dot = styled.span<{ $pulse: boolean }>`
  width: 5px;
  height: 5px;
  flex: none;
  border-radius: ${radii.pill}px;
  background: currentColor;

  ${({ $pulse }) =>
    $pulse &&
    css`
      animation: ${pulse} 1.4s infinite;

      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    `}
`;

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  /** Uppercase, as a state should be. Off for a proper noun. */
  caps?: boolean;
  /** The mono count form: shorter, not uppercase, tabular figures. */
  count?: boolean;
  /** A leading dot, for a live or in-flight state. */
  dot?: boolean;
  /** Beats the dot, for a state that is actually in flight. */
  pulse?: boolean;
}

const Badge: React.FC<BadgeProps> = ({
  tone = "gray",
  caps = true,
  count = false,
  dot = false,
  pulse: pulsing = false,
  children,
  ...props
}) => (
  <Root $tone={tone} $caps={caps} $count={count} {...props}>
    {dot && <Dot $pulse={pulsing} />}
    {children}
  </Root>
);

export default Badge;
